import React, { useEffect, useState } from "react";
import SearchDetails from "../../shared/components/SearchPageComponents/SearchDetails";
import SearchSubmitOptions from "../../shared/components/SearchPageComponents/SeachSubmitOptions";
import Databasefilters from "../../shared/components/SearchPageComponents/DatabaseFilters";
import CMSearchType from "../../shared/components/SearchPageComponents/CMSearchType";
import { useSelector } from "react-redux";
import CMsearchQuery from "../../shared/components/SearchPageComponents/CMSearchQuery";
import SearchAlgosSettings from "../../shared/components/SearchPageComponents/SearchAlgosSettings";
import CMSearchPatterns from "../../shared/components/SearchPageComponents/CMSearchPatterns";
import CMSearchService from "../../services/CMSearchService"
import { toast } from "react-toastify";
import _ from "lodash";
import { useHistory, useParams } from 'react-router-dom';
import SearchFee from "../../shared/components/SearchPageComponents/SearchFee";
import CMDatabases from "../../shared/components/SearchPageComponents/CMDatabases";
import { getDocsUrl } from '../../helpers/docsUrl';
import GQPopup from "../../shared/components/GQPopups/GQPopup";
import ContactSupportErrorModal from '../../shared/Modal/ContactSupportErrorModal';
import { useTranslation } from "react-i18next";
import { countSequences } from "../../helpers/mismatchCalculator";


const isMarkedEmptyQuery = (searchInfo) => {
    const marker = searchInfo?.qdb_seq_empty;
    return marker === "1" || marker === 1 || marker === true;
};

const hasAtLeastOneQuerySequence = (searchInfo) => {
    if (!searchInfo || isMarkedEmptyQuery(searchInfo)) return false;
    return countSequences(searchInfo.qdb_seq || "") > 0;
};

const hasPositionRangeInCriteria = (criteria) => {
    if (!criteria) return false;

    if (typeof criteria.posListStr === "string" && criteria.posListStr.trim() !== "") {
        return true;
    }

    if (!Array.isArray(criteria.posList) || criteria.posList.length === 0) {
        return false;
    }

    const hasMeaningfulPosListValue = criteria.posList.some((pos) => {
        if (!pos || typeof pos !== "object") return false;

        const keys = Object.keys(pos).filter((k) => {
            const val = pos[k];
            return val !== null && val !== undefined && (val + "").trim() !== "";
        });

        if (keys.length === 0) return false;

        // Ignore default placeholders used when creating new criteria.
        if (
            criteria.posList.length === 1 &&
            keys.length === 1 &&
            (keys[0] === "start" || keys[0] === "region") &&
            ((pos[keys[0]] + "").trim() === "1")
        ) {
            return false;
        }

        return true;
    });

    return hasMeaningfulPosListValue;
};

const getCMPatterns = (formJson) => {
    let cmFilters = formJson?.searchInfo?.cm_filters;

    if (typeof cmFilters === "string" && cmFilters.length > 0) {
        try {
            cmFilters = JSON.parse(cmFilters);
        } catch (e) {
            cmFilters = {};
        }
    }

    return Array.isArray(cmFilters?.patterns) ? cmFilters.patterns : [];
};

const canSubmitCMSearch = (formJson) => {
    const searchInfo = formJson?.searchInfo;
    const patterns = getCMPatterns(formJson);

    const hasQuery = hasAtLeastOneQuerySequence(searchInfo);

    const hasPositionRangeCriteria = patterns.some((pattern) => {
        if (!pattern || pattern.type === "OC" || !Array.isArray(pattern.cmCriteria)) return false;
        return pattern.cmCriteria.some((criteria) => hasPositionRangeInCriteria(criteria));
    });

    const hasNonAnyModification = patterns.some((pattern) => {
        if (!pattern || !Array.isArray(pattern.cmCriteria)) return false;
        return pattern.cmCriteria.some((criteria) => criteria && criteria.modType !== "AMOD");
    });

    // A OR (B OR C): query sequence OR position range criteria OR non-default modification selection.
    return hasQuery || hasPositionRangeCriteria || hasNonAnyModification;
};


const CMSearchPage = () => {
    const history = useHistory();
    const { t } = useTranslation();

    const toggle = useSelector(state => state.setToggle.toggle);
    const userInfo = useSelector(state => state.setUserInfo);

    const [hasAccess, setHasAccess] = useState(false);
    const [gqdbAccess, setGqdbAccess] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [errorHeading, setErrorHeading] = useState("");
    const [isWarningReturned, setIsWarningReturned] = useState(false);
    const [ignoreWarnings, setIgnoreWarnings] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);


    useEffect(() => {
        document.title = "Chemical Modification Search - GenomeQuest";
        if(userInfo !== null && userInfo !== undefined){
            if(Object.entries(userInfo.current_user).length !== 0){
                if(userInfo.current_user.cmAccess){
                    setHasAccess(true);
                    formJson.searchInfo.email = userInfo.current_user.email;
                    setFormJson({...formJson});
                    

                }else{
                    //redirect home
                    history.push('/home');
                }

                if(userInfo.current_user.user_class_name === "adminium" ){ // || userInfo.current_user.accounting_group_name === "GenomeQuest"
                    setGqdbAccess(true);
                }
            }
        }
    }, [userInfo]);

    
    //if page has searchID param
    const { parentId, tempname } = useParams();
    useEffect( async () => {
        if (parentId != null) {
            const response = await CMSearchService.redoCMSearch(parentId);
            if (response && response.response_status === 0) {
                formJson.layout.isRedo = true;
                formJson.searchInfo.parent_id = parentId;
                const content = response.response_content;
                formJson.searchInfo.ppuType = content.ppuType;
                formJson.layout.group_credits = content.group_credits;
                formJson.layout.group_voucher_credits = content.group_voucher_credits || 0;
                formJson.layout.voucherCreditsToUse = 0;
                formJson.layout.search_preferences = content.search_preferences;
                formJson.layout.search_preferences_pattern = content.search_preferences_pattern;
                formJson.layout.search_preferences_patterns = content.search_preferences_patterns;
                applyFormValues(content);
            }
        } else {
            if (tempname != null) {
                const response = await CMSearchService.getCMTemplate(tempname);
                if (response && response.response_status === 0) {
                    applyFormValues(response.response_content);
                }
            }

            const response = await CMSearchService.launchCMSearch();

            if(response && response.response_status === 0){
                const content = response.response_content;
                formJson.searchInfo.ppuType = content.ppuType;
                formJson.layout.group_credits = content.group_credits;
                formJson.layout.group_voucher_credits = content.group_voucher_credits || 0;
                formJson.layout.voucherCreditsToUse = 0;
                formJson.layout.search_preferences = content.search_preferences;
                formJson.layout.search_preferences_pattern = content.search_preferences_pattern;
                formJson.layout.search_preferences_patterns = content.search_preferences_patterns;

                if(content.default_preference != null){
                    const tmpResponse = await CMSearchService.getCMTemplate(content.default_preference);
                    if(tmpResponse && tmpResponse.response_status === 0){
                        applyFormValues(tmpResponse.response_content);
                    }
                }
            }
        }

        setFormJson({...formJson});

    }, [parentId, tempname]);

    function applyFormValues(startingSearchParams) {

        if(startingSearchParams.ppuType != null){
            formJson.layout.ppuType = startingSearchParams.ppuType;
        }

        if(startingSearchParams.group_credits != null){
            formJson.layout.group_credits = startingSearchParams.group_credits;
        }

        if(startingSearchParams.group_voucher_credits != null){
            formJson.layout.group_voucher_credits = startingSearchParams.group_voucher_credits;
        }
        formJson.layout.voucherCreditsToUse = 0;

        let startingContent = {};
        if(startingSearchParams.redoParams != null){
            startingContent = startingSearchParams.redoParams;
        }else if(startingSearchParams.map != null){
            startingContent = startingSearchParams.map;
        }

        let keys = Object.keys(startingContent);
        for(let i = 0; i < keys.length; i++){

            if(keys[i] === "qdb_seq"){

                if(startingContent[keys[i]] != ""){
                    if(startingContent.qdb_seq_empty !== "1"){
                        formJson.layout.hasSearchQuery = true;
                    }
                    formJson.searchInfo[keys[i]] = startingContent[keys[i]];
                }

            }else if(keys[i] === "email" && (startingContent[keys[i]]!= null && startingContent[keys[i]] !== "")){

                formJson.searchInfo[keys[i]] = startingContent[keys[i]];
                formJson.searchInfo["send_email"] = "on";

            }else if(keys[i] !== "parent_folder_id" && keys[i] !== "template_name" && keys[i] !== "PHPSESSID" && keys[i] !== "XDEBUG_SESSION" && keys[i] !== "do" && keys[i] !== "workflow_type" && keys[i] !== "format" && keys[i] !== "view_helper"){

                formJson.searchInfo[keys[i]] = startingContent[keys[i]];

            }
        }


        setFormJson({...formJson});
    }

    async function handleSearchSubmit() {

        if (!canSubmitCMSearch(formJson)) {
            return;
        }

        formJson.layout.errorsActive = true;
        if (formJson.layout.errorIdList.length === 0 ){ //if no errors

            let postData = _.cloneDeep(formJson.searchInfo);
            postData = formatPostData(postData);
            postData.voucher_credits = formJson.layout.voucherCreditsToUse || 0;
            console.log(postData);
            const response = await CMSearchService.submitCMSearch(postData);
            
            if (response.response_status === 0) {
                setSubmitSuccess(true);

                setTimeout(() => {
                    history.push('/home');
                }, 1500);
            }else if (response && response.response_status === 2 && response.response_content.qdb && response.response_content.qdb.msg && response.response_content.qdb.msg.includes("wrong query sequence type")) {
                // setWarningMsg("Warning: "+response.response_content.qdb.msg);
                setIsWarningReturned(true);
                setShowErrorModal(true);
                setErrorMsg("Warning: " + response.response_content.qdb.msg);
                setErrorHeading('Please notice the warnings below and either fix it or submit as is.')

                // window.scrollTo(0,0);
            } else if (response && response.response_status === 1 && response.response_content.qdb && response.response_content.qdb.msg) {
                // setWarningMsg("Warning: "+response.response_content.qdb.msg);
                setShowErrorModal(true);
                setErrorMsg(response.response_content.qdb.msg);
                setErrorHeading(t("seqSearchErrorOccured"))
                // window.scrollTo(0,0);
            } else {
                let setMessage = response && response.response_content && response.response_content.message ? response.response_content.message : (response === 'Error: Network Error') ? 'Network connection failed' : "Unknown";
                // setMessage = response && response.response_content && response.response_content.qdb && response && response.response_content && response.response_content.qdb.msg ? response.response_content.qdb.msg : "Unknown";
                setShowErrorModal(true);
                setErrorMsg(setMessage);
                setErrorHeading(t("seqSearchErrorOccured"))
                if (response === 'Error: Network Error') {
                    setTimeout(() => {
                        history.push('/home')
                    }, 5000)
                }
            }
        }

        setFormJson({...formJson});
    }

    async function saveTemplate() {
        const templateName = formJson.searchInfo.template_name;
        let postData = _.cloneDeep(formJson.searchInfo);
        postData = formatPostData(postData);
        const response = await CMSearchService.saveCMTemplate(postData);
        if (response !== undefined) {
            if (templateName) {
                if (!Array.isArray(formJson.layout.search_preferences)) {
                    formJson.layout.search_preferences = [];
                }
                if (!formJson.layout.search_preferences.includes(templateName)) {
                    formJson.layout.search_preferences.push(templateName);
                }
                formJson.layout.template = templateName;
            }
            setFormJson({...formJson});
            toast.success("Template saved successfully");
        } else {
            toast.error("Error saving template");
        }
    }

    function formatPostData(data) {

        data.searchtype = "FTO";
        data.title = data.title+"";
        data.nucdb_type = "multiple";
        data.protdb_type = "multiple";
        data.ignore_warning = ignoreWarnings ? "1" : "0";
       

        if(data.qdb_seq === null || data.qdb_seq === undefined || data.qdb_seq === ""){
            data.qdb_seq = "A";
            data.qdb_seq_empty="1"
            data.ignore_warning="1";
        }

        if(data.qdb_seq_empty=="1"){
            data.ignore_warning="1";
            data.qdb_seq = "A";
        }

        // if(data.send_email === ""){
        //     delete data.email;
        // }

        if(data.strat_name === null || data.strat_name === undefined){
            data.strat_name = "kerr";
            data.strat_genepast_perc_id = "100";
            data.strat_genepast_perc_id_over = "QUERY";
            data.best_hit_keep_max = "1000";
        }

        switch(data.strat_name){

            case "blast":
                delete data.strat_fragment_perc_id_nuc
                delete data.strat_fragment_perc_id_pro
                delete data.strat_fragment_window_length_nuc
                delete data.strat_fragment_window_length_pro
                delete data.strat_fragment_window_length_pro
                delete data.strat_genepast_perc_id
                delete data.strat_genepast_perc_id_over
                if(data.qdb_seq_type === "protein"){
                    delete data.strat_blast_scoring_matrix_nuc;
                    delete data.strat_blast_word_size_nuc;
                }else{
                    delete data.strat_blast_scoring_matrix_pro;
                    delete data.strat_blast_word_size_pro;
                }
                break;

            case "fragment":
                delete data.strat_blast_eval_cutoff
                delete data.strat_blast_hsp
                delete data.strat_blast_scoring_matrix_nuc
                delete data.strat_blast_scoring_matrix_pro
                delete data.strat_blast_word_size_nuc
                delete data.strat_blast_word_size_pro
                delete data.strat_genepast_perc_id
                delete data.strat_genepast_perc_id_over
                if(data.qdb_seq_type === "protein"){
                    delete data.strat_fragment_window_length_nuc;
                }else{
                    delete data.strat_fragment_window_length_pro;
                }
                break;

            default:
                delete data.strat_blast_eval_cutoff
                delete data.strat_blast_hsp
                delete data.strat_blast_scoring_matrix_nuc
                delete data.strat_blast_scoring_matrix_pro
                delete data.strat_blast_word_size_nuc
                delete data.strat_blast_word_size_pro
                delete data.strat_fragment_perc_id_nuc
                delete data.strat_fragment_perc_id_pro
                delete data.strat_fragment_window_length_nuc
                delete data.strat_fragment_window_length_pro
                delete data.strat_fragment_window_length_pro
                break;
        }
       
       
        //iterate through sdb_filters
        let sdbfilts = Object.keys(data.sdb_filters);
        let sbdString = []
        for(let i = 0; i < sdbfilts.length; i++){
            const key = sdbfilts[i];
            const value = data.sdb_filters[key];

            if(value !== null && value !== undefined && value !== ""){
                if(value.active === true){
                    let newsdbObj ={
                        P: value.P,
                        O: value.O,
                        V: value.V
                    }

                    if(value.P === "SEQUENCE_D1" || value.P === "SEQUENCE_D2"){

                        //convert date such as: 2025-08-20T14:42:43.000Z to 20250820 and make it a string
                        newsdbObj.V = (new Date(value.V)).toISOString().split('T')[0].replace(/-/g, "");
                    }

                    sbdString.push(JSON.stringify(newsdbObj));
                }
            }
        }
        sbdString = "["+sbdString+"]";
        data.sdb_filters = sbdString;

        if(data.cm_filters.mode !== "CUSTOM"){
            delete data.cm_filters.query;
        }

        if(data.cm_filters.template_name === "" || data.cm_filters.template_name === null){
            delete data.cm_filters.template_name;
        }else{
            data.cm_filters.template_name = escapeThis(data.cm_filters.template_name);
        }

        for(let i = 0; i < data.cm_filters.patterns.length; i++){

            if(data.cm_filters.patterns[i].type !== "FP"){
                delete data.cm_filters.patterns[i].region;
                delete data.cm_filters.patterns[i].posList;
                delete data.cm_filters.patterns[i].floatType;
            }

            delete data.cm_filters.patterns[i].posListStr;

            if(data.cm_filters.patterns[i].filterName === "" || data.cm_filters.patterns[i].filterName === null){
                    delete data.cm_filters.patterns[i].filterName;
            }else{
                data.cm_filters.patterns[i].filterName = escapeThis(data.cm_filters.patterns[i].filterName);
            }


            for(let j = 0; j < data.cm_filters.patterns[i].cmCriteria.length; j++){
                
                if(data.cm_filters.patterns[i].cmCriteria[j].codes !=null){
                    if(Array.isArray(data.cm_filters.patterns[i].cmCriteria[j].codes)){
                        for(let k = 0; k < data.cm_filters.patterns[i].cmCriteria[j].codes.length; k++){
                            let code = data.cm_filters.patterns[i].cmCriteria[j].codes[k];
                            if(code === "" || code === null){
                                data.cm_filters.patterns[i].cmCriteria[j].codes.splice(k, 1);
                                k--;
                            }else
                            if(typeof code === "string" && code.startsWith("{")){
                                let codeObj = JSON.parse(code);
                                // Extract values array from the parsed object
                                if(codeObj.values && Array.isArray(codeObj.values)){
                                    // Replace the JSON string with the values array
                                    data.cm_filters.patterns[i].cmCriteria[j].codes.splice(k, 1, ...codeObj.values);
                                    k += codeObj.values.length - 1; // Adjust index since we added multiple items
                                }
                            }
                        }
                    }
                }

                if(JSON.stringify(data.cm_filters.patterns[i].cmCriteria[j].posList) === "[]"){
                    delete data.cm_filters.patterns[i].cmCriteria[j].posList;
                }
                // if(JSON.stringify(data.cm_filters.patterns[i].cmCriteria[j].codes) === "[]"){
                //     delete data.cm_filters.patterns[i].cmCriteria[j].codes;
                // }
                if(data.cm_filters.patterns[i].type === "OC"){
                    //delete data.cm_filters.patterns[i].cmCriteria[j].type;
                    //delete data.cm_filters.patterns[i].cmCriteria[j].modType; 
                    delete data.cm_filters.patterns[i].cmCriteria[j].posList;
                }else{
                    delete data.cm_filters.patterns[i].cmCriteria[j].countType;
                    delete data.cm_filters.patterns[i].cmCriteria[j].count;
                    //delete data.cm_filters.patterns[i].cmCriteria[j].modType; 
                }

                delete data.cm_filters.patterns[i].cmCriteria[j].posListStr;
                delete data.cm_filters.patterns[i].cmCriteria[j].countStr;
                
            }

        }

        data.cm_filters = JSON.stringify(data.cm_filters);

        const hasQuerySeq = formJson.layout.hasSearchQuery && formJson.searchInfo.qdb_seq_empty !== "1";

        if(!hasQuerySeq){
            delete data.query_cm_filters;
        }

        if(hasQuerySeq && data.query_cm_filters && typeof data.query_cm_filters === 'object' && Array.isArray(data.query_cm_filters.patterns)){
            const hasAnyCodes = data.query_cm_filters.patterns.some(p =>
                Array.isArray(p.cmCriteria) && p.cmCriteria.some(c => Array.isArray(c.codes) && c.codes.length > 0)
            );
            if(!hasAnyCodes){
                delete data.query_cm_filters;
            }
        }

        if(hasQuerySeq && data.query_cm_filters && typeof data.query_cm_filters === 'object' && Array.isArray(data.query_cm_filters.patterns)){

            if(data.query_cm_filters.mode !== "CUSTOM"){
                delete data.query_cm_filters.query;
            }

            if(data.query_cm_filters.template_name === "" || data.query_cm_filters.template_name === null){
                delete data.query_cm_filters.template_name;
            }else{
                data.query_cm_filters.template_name = escapeThis(data.query_cm_filters.template_name);
            }

            for(let i = 0; i < data.query_cm_filters.patterns.length; i++){

                if(data.query_cm_filters.patterns[i].type !== "FP"){
                    delete data.query_cm_filters.patterns[i].region;
                    delete data.query_cm_filters.patterns[i].posList;
                    delete data.query_cm_filters.patterns[i].floatType;
                }

                delete data.query_cm_filters.patterns[i].posListStr;

                if(data.query_cm_filters.patterns[i].filterName === "" || data.query_cm_filters.patterns[i].filterName === null){
                    delete data.query_cm_filters.patterns[i].filterName;
                }else{
                    data.query_cm_filters.patterns[i].filterName = escapeThis(data.query_cm_filters.patterns[i].filterName);
                }

                for(let j = 0; j < data.query_cm_filters.patterns[i].cmCriteria.length; j++){

                    if(data.query_cm_filters.patterns[i].cmCriteria[j].codes != null){
                        if(Array.isArray(data.query_cm_filters.patterns[i].cmCriteria[j].codes)){
                            for(let k = 0; k < data.query_cm_filters.patterns[i].cmCriteria[j].codes.length; k++){
                                let code = data.query_cm_filters.patterns[i].cmCriteria[j].codes[k];
                                if(code === "" || code === null){
                                    data.query_cm_filters.patterns[i].cmCriteria[j].codes.splice(k, 1);
                                    k--;
                                }else if(typeof code === "string" && code.startsWith("{")){
                                    let codeObj = JSON.parse(code);
                                    if(codeObj.values && Array.isArray(codeObj.values)){
                                        data.query_cm_filters.patterns[i].cmCriteria[j].codes.splice(k, 1, ...codeObj.values);
                                        k += codeObj.values.length - 1;
                                    }
                                }
                            }
                        }
                    }

                    if(JSON.stringify(data.query_cm_filters.patterns[i].cmCriteria[j].posList) === "[]"){
                        delete data.query_cm_filters.patterns[i].cmCriteria[j].posList;
                    }

                    if(data.query_cm_filters.patterns[i].type === "OC"){
                        delete data.query_cm_filters.patterns[i].cmCriteria[j].posList;
                    }else{
                        delete data.query_cm_filters.patterns[i].cmCriteria[j].countType;
                        delete data.query_cm_filters.patterns[i].cmCriteria[j].count;
                    }

                    delete data.query_cm_filters.patterns[i].cmCriteria[j].posListStr;
                    delete data.query_cm_filters.patterns[i].cmCriteria[j].countStr;
                }
            }

            data.query_cm_filters = JSON.stringify(data.query_cm_filters);
        }

        return data;
    }

    const [formJson, setFormJson] = useState({

        layout:{
            syscontrol_search_submit: false,
            syscontrol_search_text: "",
            errorsActive: false,
            errorIdList: [],
            isRedo: false
        },
        searchInfo:{
            // title: "", 
            // qdb_seq: "",
            // qdb_seq_type: "nucleotide",  
            // patterns: "",    
            // seqlenrange_low: "",
            // seqlenrange_high: "",
            //best_hit_keep_max: "",
            // sdb_filters: "",
            template_name: "",
            is_default: false,
            parent_id: "",
            email: "",
            send_email: false,
        },
    })

    function escapeThis(str) {
        return encodeURIComponent(str);
    }

    function handleErrorModal(action) {
        if (action !== 'cancel' && isWarningReturned) {
            // User chose to continue editing after seeing a warning; bypass warning on next submit.
            setIgnoreWarnings(true);
        }
        setShowErrorModal(!showErrorModal);
    }

    let subjectText = "GenomeQuest: Error updating account information [Error code: " + errorMsg + "]";


    return (
        <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
        {hasAccess &&
            <div style={{width: "90%", maxWidth: "1000px"}}>

                {/*Search Details*/}
                <div>
                    <SearchDetails
                        formJson={formJson}
                        setFormJson={(data)=>setFormJson(data)}
                        selectSearchTemplate={async (template)=>{
                            const tmpResponse = await CMSearchService.getCMTemplate(template);
                            if(tmpResponse){
                                if(tmpResponse.response_status === 0){
                                    formJson.layout.template = template;
                                    applyFormValues(tmpResponse.response_content, false);
                                }
                            }
                        }}
                        linkRef={getDocsUrl('chemical_modification.html')}
                    >
                    </SearchDetails>
                </div>
                <hr style={toggle ? {backgroundColor: '#dfdfdf'}: {backgroundColor: '#aea9a9'}}/>

                {/*Search Type*/}
                <div>
                    <CMSearchType
                        formJson={formJson}
                        setFormJson={(data)=>setFormJson({...data})}
                    >
                    </CMSearchType>
                </div>
                {formJson.layout.hasSearchQuery && formJson.searchInfo.qdb_seq_empty !== "1" &&
                <>
                <div>
                    <CMsearchQuery
                        formJson={formJson}
                        setFormJson={(data)=>setFormJson({...data})}
                    />
                </div>
                <hr style={toggle ? {backgroundColor: '#dfdfdf'}: {backgroundColor: '#aea9a9'}}/>
                <div>
                    <SearchAlgosSettings
                        formJson={formJson}
                        setFormJson={(data)=>setFormJson({...data})}
                    />
                </div>
                </>
                }
                <hr style={toggle ? {backgroundColor: '#dfdfdf'}: {backgroundColor: '#aea9a9'}}/>
                {/*Query Search Patterns — shown only when query sequence is present*/}
                {formJson.layout.hasSearchQuery && formJson.searchInfo.qdb_seq_empty !== "1" &&
                <div>
                    <CMSearchPatterns
                        formJson={formJson}
                        setFormJson={(data)=>setFormJson({...data})}
                        filtersKey="query_cm_filters"
                        title="Query Search Patterns"
                        centerTitle={false}
                        qSeqInstead={true}
                    >
                    </CMSearchPatterns>
                </div>
                }
                {/*Subject Search Patterns*/}
                <div>
                    <CMSearchPatterns
                        formJson={formJson}
                        setFormJson={(data)=>setFormJson({...data})}
                        title="Subject Search Patterns"
                    >
                    </CMSearchPatterns>
                </div>
                <hr style={toggle ? {backgroundColor: '#dfdfdf'}: {backgroundColor: '#aea9a9'}}/>

                {/*Database Filters*/}
                <div>
                    <Databasefilters
                        formJson={formJson}
                        setFormJson={(data)=>setFormJson({...data})}
                    >
                    </Databasefilters>
                </div>

                <hr style={toggle ? {backgroundColor: '#dfdfdf'}: {backgroundColor: '#aea9a9'}}/>
                    <CMDatabases
                        formJson={formJson}
                        setFormJson={(data)=>setFormJson({...data})}
                        gqdbAccess={gqdbAccess}
                    />

                <hr style={toggle ? {backgroundColor: '#dfdfdf'}: {backgroundColor: '#aea9a9'}}/>
                {formJson.searchInfo.ppuType != "0" && formJson.searchInfo.ppuType != null &&
                    <div>
                        <SearchFee
                            formJson={formJson}
                            setFormJson={(data)=>setFormJson({...data})}
                            toggle={toggle}
                        />
                        <hr style={toggle ? {backgroundColor: '#dfdfdf'}: {backgroundColor: '#aea9a9'}}/>
                    </div>
                }
                {/*Submit*/}
                <div>
                    <SearchSubmitOptions
                        formJson={formJson}
                        setFormJson={(data)=>setFormJson({...data})}
                        canSubmitSearch={canSubmitCMSearch(formJson)}
                        submitSearch={() => handleSearchSubmit()}
                        saveTemplate={() => {saveTemplate()}}
                    >
                    </SearchSubmitOptions>
                </div>
                <hr style={toggle ? {backgroundColor: '#dfdfdf'}: {backgroundColor: '#aea9a9'}}/>
            </div>
        }
            <ContactSupportErrorModal
                show={showErrorModal}
                errorCode={errorMsg}
                modalCallBack={handleErrorModal}
                subjectText={subjectText}
                errorContent={errorHeading}
                isWarning={isWarningReturned}
                type="editSearch" // When clicking cancel button, only close the modal dialog
            />
            <GQPopup
                show={submitSuccess}
                close={() => setSubmitSuccess(false)}
                innerHtml={
                    <div className="appTextColor text-center" style={{padding: "0px", minWidth: "520px", textAlign: "center"}}>
                        
                            <h5 style={{padding: "30px", height: "90px", backgroundColor: "lightgray", borderRadius: '8px' }}>Your search has been submitted</h5>
                    </div>
                }
            />
        </div>
    )
}


export default CMSearchPage;